import {
  type FetchResult,
  type MutationResult,
  type SendInboxDraftMutation,
  useSendInboxDraftMutation,
} from "@qw/graphql";
import { Icon, useOpenConfirmation, useToast } from "@qw/ui";
import { useCallback } from "react";
import {
  type PendingAttachmentUploadsOptions,
  pendingAttachmentUploadsSendWarning,
} from "@/utils/pending-attachment-uploads";

import { useDraftsCompose } from "./use-drafts-compose";

export type UseSendDraftReturn = [
  (id: string, options?: PendingAttachmentUploadsOptions) => Promise<FetchResult<SendInboxDraftMutation> | undefined>,
  MutationResult<SendInboxDraftMutation>,
];

export const useSendDraft = (): UseSendDraftReturn => {
  const { toast } = useToast();
  const { closeDraft } = useDraftsCompose();
  const { openConfirmation } = useOpenConfirmation();
  const [sendDraftMutation, result] = useSendInboxDraftMutation();

  const sendDraft = useCallback(
    async (id: string, { pendingAttachmentUploadCount = 0 }: PendingAttachmentUploadsOptions = {}) => {
      const { title, description, confirmLabel } =
        pendingAttachmentUploadCount > 0
          ? {
              title: "Attachments still uploading",
              description: pendingAttachmentUploadsSendWarning(pendingAttachmentUploadCount),
              confirmLabel: "Send anyway",
            }
          : {
              title: "Send draft",
              description: "Are you sure you want to send this draft? This action cannot be undone.",
              confirmLabel: "Send",
            };

      const confirmed = await openConfirmation({
        title,
        description,
        buttonProps: {
          theme: "primary",
          children: (
            <>
              <Icon icon="send" /> {confirmLabel}
            </>
          ),
        },
      });

      if (!confirmed) {
        return;
      }

      return sendDraftMutation({
        variables: { id },
        onCompleted: (data) => {
          if (!data?.sendInboxDraft) {
            throw new Error("Failed to send draft");
          }

          toast({
            title: "Draft sent successfully",
            description: "Your email has been sent.",
          });
          closeDraft(id);
        },
        onError: (error) => {
          toast({
            title: "Failed to send draft",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
        refetchQueries: ["InboxDrafts", "InboxDraft", "InboxConversation", "InboxConversations", "InboxDraftsCount"],
      });
    },
    [sendDraftMutation, openConfirmation, toast, closeDraft]
  );

  return [sendDraft, result];
};
